import { PageInfo } from "@/components/Pagination";
import { CRM_NOTE_FIELDS_FRAGMENT } from "@/fragments";
import { PAGE_INFO_FRAGMENT } from "@/hooks/usePagination";
import { TypedDocumentNode, gql } from "@apollo/client";

interface Registrant {
  pgaId: string;
  profilePhoto: string;
  programType: string;
  registeredAt: string;
  registrantName: string;
  sessionCount: number;
}

type Query = {
  eventRegistrants: {
    totalCount: number;
    currentPage?: number;
    nodes: Registrant[];
    pageInfo: PageInfo;
  };
  lateCancelRegistrants: {
    totalCount: number;
    nodes: Registrant[];
  };
};

export const EVENT_REGISTRANTS_QUERY: TypedDocumentNode<Query> = gql`
  query GetEventRegistrants(
    $eventSlug: String!
    $search: String
    $first: Int
    $last: Int
    $after: String
    $before: String
    $status: BackofficeEventsRegistrationStatusEnum
    $ids: [String!]
    $aroundId: ID
  ) {
    eventRegistrants(
      slug: $eventSlug
      search: $search
      first: $first
      last: $last
      after: $after
      before: $before
      status: $status
      ids: $ids
      aroundId: $aroundId
    ) {
      edges {
        node {
          id
          pgaId
          profilePhoto
          programType
          registeredAt
          canceledAt
          registrantName
          sessionCount
          customFields
          fulfillableRequirementId
          university {
            id
            name
          }
          sessionRegistrations {
            id
            attended
          }
          order {
            status
            totalPaid
            totalRefunded
            paymentStatus
          }
          notes {
            ...CRMNoteFields
          }
          patSubmission {
            id
            roundOne {
              roundScore
              meetsQualifyingScore
            }
            roundTwo {
              roundScore
              meetsQualifyingScore
            }
            targetScore
            totalScore
            additionalStatus
            note
            result
            date
            suspension {
              startDate
              endDate
            }
            document {
              id
              url
              name
            }
          }
        }
      }
      currentPage
      totalCount
      ...PageInfo
    }
  }
  ${PAGE_INFO_FRAGMENT}
  ${CRM_NOTE_FIELDS_FRAGMENT}
`;

export const EVENT_LEADS_QUERY: TypedDocumentNode<Query> = gql`
  query GetEventLeads($slug: String!, $status: BackofficeEventsRegistrationStatusEnum, $search: String) {
    eventRegistrants(slug: $slug, status: $status, search: $search) {
      totalCount
      nodes {
        id
        registrantName
        pgaId
        email
        externalId
        phoneNumbers {
          phoneNumber
          phoneType
          phoneFormatted
        }
        programType
        registeredAt
        sessionCount
        profilePhoto
        customFields
        order {
          id
        }
        sessionRegistrations {
          id
          attended
        }
        recruitmentLead {
          id
          referralSource
          entryPoint
          zipcode
          recruitmentDate
        }
      }
    }
  }
`;

export const LATE_CANCEL_REGISTRANTS_QUERY: TypedDocumentNode<Query> = gql`
  query LateCancelRegistrants($slug: String!) {
    lateCancelRegistrants(slug: $slug) {
      edges {
        node {
          customFields
          id
          pgaId
          profilePhoto
          programType
          registrantName
          patSubmission {
            additionalStatus
            patType
            suspension {
              endDate
              startDate
            }
            document {
              id
              url
              name
            }
          }
        }
      }
    }
  }
`;

export const EVENT_WITH_ACTIVITY_PARTICIPATIONS_QUERY = gql`
  query GetBackofficeEventWithActivityParticipations($slug: String!, $hostProfessionalId: ID, $examinerId: ID) {
    event(slug: $slug) {
      id
      activityType {
        hostProfessionalParticipations: participations(
          filters: { personId: $hostProfessionalId, roles: [HOST_PROFESSIONAL] }
        ) {
          nodes {
            id
            person {
              pgaId
            }
            activity {
              event {
                slug
              }
            }
          }
        }
        examinerParticipations: participations(filters: { personId: $examinerId, roles: [EXAMINER] }) {
          nodes {
            id
            person {
              pgaId
            }
            activity {
              event {
                slug
              }
            }
          }
        }
      }
    }
  }
`;

export const UNIVERSITY_EVENT_QUERY = gql`
  query GetUniversityBackofficeEvent($slug: String!) {
    event(slug: $slug) {
      id
      activityTypeSummary {
        id
        title
      }
      status
      draft
      title
      description
      hasWaitlist
      waitlistEnabled
      cancelledAt
      finalizedAt
      endsAt
      startsAt
      registrationOpenAt
      registrationCloseAt
      hasLateCancellation
      termsContent
      sessionCount
      collectsAttendance
      registrationCount
      registrationType
      hasActiveOrders
      timezone
      bannerImageUrl
      eventCode
      overrideRoles
      updatedAt
      createdBy {
        id
        name
      }
      eventFees {
        id
        paymentCollection
        displayName
        amountCents
        productVariant {
          id
          name
          priceCents
        }
      }
      eventTypeSummary {
        id
        name
      }
      facility {
        id
        name
      }
      universities {
        id
        name
      }
      locationType
      location
      address1
      city
      state
      zip
      patEventAttributes {
        finalized
        yardageMale
        courseRatingMale
        yardageMaleFiftyPlus
        yardageFemale
        courseRatingFemale
        yardageFemaleFiftyPlus
        targetScoreMale
        targetScoreFemale
        collectionType
        registrationFeeCents
        courseFeeCents
        hostProfessional {
          id
          pgaId
          displayName
          pdrRequirements {
            requirements {
              label
            }
          }
          contactAddress {
            address1
            city
            state
            postalCode
          }
        }
        examiner {
          id
          pgaId
          displayName
          pdrRequirements {
            requirements {
              label
            }
          }
          contactAddress {
            address1
            city
            state
            postalCode
          }
        }
        courseConditions
        problemsIssues
        examinerFeePayee
        submittedForPaymentAt
      }
      sessions {
        id
        startsAt
        endsAt
        maxAttendance
        __typename
      }
      customFields
      section {
        id
        name
        legacyId
        permissions {
          manageEvents
        }
      }
      totalFeesCollected
      registrationFeesCollected
      courseFeesCollected
      recruitmentLeadFormUrl
      recruitmentEventAttributes {
        id
        isFinalized
        additionalComments
        commentsAndQuotes
        createdAt
        estimatedCoaches
        estimatedExpenses
        estimatedOtherAttendees
        estimatedParents
        eventRating
        eventRecap
        overallFeedback
        photoUploads
        sponsorshipAsk
        totalAttendees
        updatedAt
        recruitmentEventType
        purpose
        strategicFunding
        pgaRepresentation
        supportRequested
        staffAttendees {
          id
          displayName
          email
          pgaId
          profilePhoto
          primarySection {
            id
            name
          }
          phoneNumbers {
            phoneNumber
            phoneType
            phoneFormatted
          }
        }
      }
    }
  }
`;

export const EVENT_QUERY = gql`
  query GetBackofficeEvent($slug: String!) {
    event(slug: $slug) {
      id
      activityType {
        id
        title
        sectionAvailability
        createsEvent
        config {
          creditRates {
            id
            rateMultiplier
            rateUnit
            participationRole
          }
        }
      }
      status
      draft
      title
      description
      hasWaitlist
      waitlistEnabled
      cancelledAt
      finalizedAt
      endsAt
      startsAt
      registrationOpenAt
      registrationCloseAt
      hasLateCancellation
      termsContent
      sessionCount
      collectsAttendance
      registrationCount
      registrationType
      hasActiveOrders
      timezone
      bannerImageUrl
      eventCode
      overrideRoles
      updatedAt
      createdBy {
        id
        name
      }
      eventFees {
        id
        paymentCollection
        displayName
        amountCents
        productVariant {
          id
          name
          priceCents
        }
      }
      eventType {
        id
        name
      }
      facility {
        id
        name
      }
      universities {
        id
        name
      }
      locationType
      location
      address1
      city
      state
      zip
      patEventAttributes {
        finalized
        yardageMale
        courseRatingMale
        yardageMaleFiftyPlus
        yardageFemale
        courseRatingFemale
        yardageFemaleFiftyPlus
        targetScoreMale
        targetScoreFemale
        collectionType
        registrationFeeCents
        courseFeeCents
        hostProfessional {
          id
          pgaId
          displayName
          pdrRequirements {
            requirements {
              label
            }
          }
          contactAddress {
            address1
            city
            state
            postalCode
          }
        }
        examiner {
          id
          pgaId
          displayName
          pdrRequirements {
            requirements {
              label
            }
          }
          contactAddress {
            address1
            city
            state
            postalCode
          }
        }
        courseConditions
        problemsIssues
        examinerFeePayee
        submittedForPaymentAt
      }
      sessions {
        id
        startsAt
        endsAt
        maxAttendance
        __typename
      }
      customFields
      section {
        id
        name
        legacyId
        permissions {
          manageEvents
        }
      }
      totalFeesCollected
      registrationFeesCollected
      courseFeesCollected
      recruitmentLeadFormUrl
      recruitmentEventAttributes {
        id
        isFinalized
        additionalComments
        commentsAndQuotes
        createdAt
        estimatedCoaches
        estimatedExpenses
        estimatedOtherAttendees
        estimatedParents
        eventRating
        eventRecap
        overallFeedback
        photoUploads
        sponsorshipAsk
        totalAttendees
        updatedAt
        recruitmentEventType
        purpose
        strategicFunding
        pgaRepresentation
        supportRequested
        staffAttendees {
          id
          displayName
          email
          pgaId
          profilePhoto
          primarySection {
            id
            name
          }
          phoneNumbers {
            phoneNumber
            phoneType
            phoneFormatted
          }
        }
      }
    }
  }
`;

export const EVENT_WAITLIST_COUNT_QUERY = gql`
  query GetEventWaitlistCount($eventSlug: String!) {
    eventWaitlistEntries(slug: $eventSlug, first: 0) {
      totalCount
    }
  }
`;

export const GENERATE_REGISTRATION_EXPORT_MUTATION = gql`
  mutation generateRegistrationExport($input: BackofficeEventsGenerateRegistrationExportInput!) {
    generateRegistrationExport(input: $input) {
      url
      success
      message
    }
  }
`;

export const GENERATE_CANCELLATION_EXPORT_MUTATION = gql`
  mutation generateCancellationExport($input: BackofficeEventsGenerateCancellationExportInput!) {
    generateCancellationExport(input: $input) {
      url
      success
      message
    }
  }
`;

export const CANCEL_EVENT_REGISTRATION_MUTATION = gql`
  mutation CancelEventRegistration($id: ID!, $suspend: Boolean, $issueRefund: Boolean, $refundAmountCents: Int) {
    cancelEventRegistration(
      id: $id
      suspend: $suspend
      issueRefund: $issueRefund
      refundAmountCents: $refundAmountCents
    ) {
      success
      message
    }
  }
`;

export const UPDATE_EVENT_MUTATION = gql`
  mutation UpdateEvent($input: BackofficeEventsUpdateEventInput!) {
    updateEvent(input: $input) {
      success
      message
    }
  }
`;

export const CANCEL_EVENT_MUTATION = gql`
  mutation CancelEvent($id: ID!) {
    cancelEvent(id: $id) {
      success
      message
    }
  }
`;

export const SCAFFOLD_EVENT_REGISTRANTS = gql`
  mutation ScaffoldEventRegistrants($id: ID!) {
    scaffoldEventRegistrants(id: $id) {
      success
      message
    }
  }
`;

export const PAT_WORKSHEET_MUTATION = gql`
  mutation GenerateWorksheet($id: ID!) {
    patGenerateWorksheet(id: $id) {
      success
      message
      url
    }
  }
`;

export interface RecruitmentLead {
  id: string;
  pgaId: string;
  displayName: string;
  email?: string;
  profilePhoto?: string;
  phoneNumbers?: {
    phoneNumber: string;
    phoneType: string;
    phoneFormatted: string;
  }[];
  recruitmentDate?: string;
  isRecruitmentLead: boolean;
  recruitmentLead?: {
    id: string;
    referralSource?: string;
    entryPoint?: string;
    zipcode?: string;
    recruitmentDate: string;
  };
}

export interface RecruitmentLeadNode {
  node: RecruitmentLead;
}

export interface RecruitmentLeadsQueryResponse {
  people: {
    totalCount: number;
    edges: RecruitmentLeadNode[];
    pageInfo: PageInfo;
  };
}

export interface RecruitmentLeadsQueryVariables {
  first?: number;
  after?: string;
  input?: {
    search?: string;
  };
}

type EventEligibilityQuery = {
  eventEligibility: {
    isEligible: boolean;
    eligibilityErrors: string[] | null;
  };
};

type EventEligibilityVariables = {
  eventId: string;
  pgaId: string;
};

export const RECRUITMENT_LEADS_QUERY = gql`
  query GetRecruitmentLeads($first: Int, $after: String, $input: BackofficeCRMPeopleInput) {
    people(first: $first, after: $after, input: $input) {
      edges {
        node {
          id
          pgaId
          displayName
          email
          profilePhoto
          phoneNumbers {
            phoneNumber
            phoneType
            phoneFormatted
          }
          recruitmentDate
          isRecruitmentLead
          recruitmentLead {
            id
            referralSource
            entryPoint
            zipcode
            recruitmentDate
          }
        }
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

export const CREATE_EVENT_REGISTRATION_MUTATION = gql`
  mutation CreateEventRegistration($input: BackofficeCreateEventRegistrationInput!) {
    createEventRegistration(input: $input) {
      success
      message
    }
  }
`;

export const EVENT_ELIGIBILITY_QUERY: TypedDocumentNode<EventEligibilityQuery, EventEligibilityVariables> = gql`
  query EventEligibility($eventId: ID!, $pgaId: ID!) {
    eventEligibility(eventId: $eventId, pgaId: $pgaId) {
      isEligible
      eligibilityErrors
    }
  }
`;

export const UPDATE_EVENT_REGISTRATION_MUTATION = gql`
  mutation UpdateEventRegistration($input: BackofficeUpdateEventRegistrationInput!) {
    updateEventRegistration(input: $input) {
      success
      message
    }
  }
`;

export const TRANSFER_ELIGIBLE_EVENTS_QUERY = gql`
  query EligibleEventsForTransfer($eventId: ID, $pgaId: String!, $first: Int, $search: String) {
    eligibleEvents(eventId: $eventId, pgaId: $pgaId, first: $first, search: $search) {
      edges {
        node {
          id
          title
          slug
          startsAt
          endsAt
          city
          state
          eventCode
          facility {
            id
            name
          }
        }
      }
      totalCount
    }
  }
`;

export const TRANSFER_EVENT_REGISTRATION_MUTATION = gql`
  mutation TransferEventRegistration($input: BackofficeEventsTransferEventRegistrationInput!) {
    transferEventRegistration(input: $input) {
      success
      message
    }
  }
`;

export interface FinalizeSeminarRegistrationsMutationData {
  finalizeSeminarRegistrations: {
    success: boolean;
    message: string | null;
    code: string;
    registeredCount: number;
    alreadyRegisteredCount: number;
    failedCount: number;
  };
}

export interface FinalizeSeminarRegistrationsMutationVariables {
  input: {
    eventId: string;
    universityLegacyId?: string;
    pgaId?: string;
  };
}

export const FINALIZE_SEMINAR_REGISTRATIONS_MUTATION: TypedDocumentNode<
  FinalizeSeminarRegistrationsMutationData,
  FinalizeSeminarRegistrationsMutationVariables
> = gql`
  mutation FinalizeSeminarRegistrations($input: BackofficeEventsFinalizeSeminarRegistrationsInput!) {
    finalizeSeminarRegistrations(input: $input) {
      success
      message
      code
      registeredCount
      alreadyRegisteredCount
      failedCount
    }
  }
`;
